import { ProductRewardSearchResult } from "@brandclub/common-ui";
import { graphqlFetch } from "./graphqlFetch";

export enum ProductOperation {
  UnauthenticatedSearch = "UnauthenticatedSearchProducts",
  AuthenticatedSearch = "SearchProducts",
  SemanticProductSearch = "SemanticProductSearch",
}

export const UNAUTHENTICATED_SEARCH_PRODUCTS = /* GraphQL */ `
  query UnauthenticatedSearchProducts(
    $titleInclude: [String!]
    $titleExclude: [String!]
    $stacklineSkus: [String!]
    $stacklineSkusExclude: [String!]
    $campaignTypes: [String!]
    $retailPrice: [RangeFilter!]
    $reviewsRating: RangeFilter
    $reviewsCount: RangeFilter
    $brandIds: [Float!]
    $departmentIds: [Float!]
    $categoryIds: [Float!]
    $subCategoryIds: [Float!]
    $retailerIds: [Float!]
    $isBrandClubReady: Boolean
    $totalRewardAmount: RangeFilter
    $page: PageFilter!
    $sort: [SortFilter!]
    $facetFieldNames: [String!]
    $computeFacets: Boolean!
    $applySpellingCheck: Boolean
    $treatment: TreatmentFilter
    $skipAvailabilityCheck: Boolean
  ) {
    ${ProductOperation.UnauthenticatedSearch}(
      titleInclude: $titleInclude
      titleExclude: $titleExclude
      stacklineSkus: $stacklineSkus
      stacklineSkusExclude: $stacklineSkusExclude
      campaignTypes: $campaignTypes
      retailPrice: $retailPrice
      reviewsRating: $reviewsRating
      totalRewardAmount: $totalRewardAmount
      reviewsCount: $reviewsCount
      brandIds: $brandIds
      categoryIds: $categoryIds
      departmentIds: $departmentIds
      subCategoryIds: $subCategoryIds
      retailerIds: $retailerIds
      isBrandClubReady: $isBrandClubReady
      page: $page
      sort: $sort
      facetFieldNames: $facetFieldNames
      computeFacets: $computeFacets
      applySpellingCheck: $applySpellingCheck
      treatment: $treatment
      skipAvailabilityCheck: $skipAvailabilityCheck
    ) {
      products {
        affiliateRewardAmount
        brandId
        brandName
        hasActiveInstantReward
        imageUrls
        retailerId
        retailerSku
        retailPrice
        checkoutEligibility
        stacklineSku
        title
        totalRewardAmount
        retailerProductPageUrl
        dtcProductPageUrl
        isAvailable
        hasFirstPartyOffer
        offers
        retailerLogo
        retailerShortLogo
        scores {
          maxPurchaseRewardPercent
          maxPurchaseRewardAmount
          maxAffiliateRewardPercent
          maxAffiliateRewardAmount
          totalReviewRewardFeesAmount
        }
        matchedProducts {
          retailPrice
          retailerId
          retailerProductPageUrl
          dtcProductPageUrl
          totalRewardAmount
          stacklineSku
          retailerSku
          checkoutEligibility
          isAvailable
          hasFirstPartyOffer
          offers
          retailerLogo
          retailerShortLogo
          campaignRewards {
            campaignId
            campaignType
            rewardAmount
            rewardAmountType
            rewardUnlockStrategy
            campaignStatus
          }
        }
        campaignRewards {
          campaignStatus
          campaignType
          rewardAmount
          rewardAmountType
          rewardUnlockStrategy
        }
      }
      totalProducts
      facets
      suggestedTerm
    }
  }
`;

export const AUTHENTICATED_SEARCH_PRODUCTS = /* GraphQL */ `
  query searchProductsWithFullDetails(
    $titleInclude: [String!]
    $titleExclude: [String!]
    $stacklineSkus: [String!]
    $stacklineSkusExclude: [String!]
    $campaignTypes: [String!]
    $retailPrice: [RangeFilter!]
    $reviewsRating: RangeFilter
    $reviewsCount: RangeFilter
    $brandIds: [Float!]
    $departmentIds: [Float!]
    $categoryIds: [Float!]
    $subCategoryIds: [Float!]
    $retailerIds: [Float!]
    $isBrandClubReady: Boolean
    $totalRewardAmount: RangeFilter
    $page: PageFilter!
    $sort: [SortFilter!]
    $facetFieldNames: [String!]
    $computeFacets: Boolean!
    $applySpellingCheck: Boolean
    $skipAvailabilityCheck: Boolean
  ) {
    ${ProductOperation.AuthenticatedSearch}(
      titleInclude: $titleInclude
      titleExclude: $titleExclude
      stacklineSkus: $stacklineSkus
      stacklineSkusExclude: $stacklineSkusExclude
      campaignTypes: $campaignTypes
      retailPrice: $retailPrice
      reviewsRating: $reviewsRating
      totalRewardAmount: $totalRewardAmount
      reviewsCount: $reviewsCount
      brandIds: $brandIds
      categoryIds: $categoryIds
      departmentIds: $departmentIds
      subCategoryIds: $subCategoryIds
      retailerIds: $retailerIds
      isBrandClubReady: $isBrandClubReady
      page: $page
      sort: $sort
      facetFieldNames: $facetFieldNames
      computeFacets: $computeFacets
      applySpellingCheck: $applySpellingCheck
      skipAvailabilityCheck: $skipAvailabilityCheck
    ) {
      products {
        checkoutEligibility
        stacklineSku
        title
        brandName
        brandId
        categoryId
        subCategoryId
        categoryName
        retailerId
        retailerSku
        retailPrice
        reviewsRating
        groupId
        groupName
        reviewsCount
        searchScore
        totalRewardAmount
        isAvailable
        hasFirstPartyOffer
        offers
        scores {
          maxPurchaseRewardPercent
          maxPurchaseRewardAmount
          maxAffiliateRewardPercent
          maxAffiliateRewardAmount
          totalReviewRewardFeesAmount
        }
        hasActiveInstantReward
        featureBullets
        productPromotions {
          promoType
          isActive
          listPrice
          promoPrice
        }
        imageUrls
        matchedProducts {
          retailPrice
          retailerId
          retailerProductPageUrl
          dtcProductPageUrl
          totalRewardAmount
          stacklineSku
          retailerSku
          checkoutEligibility
          isAvailable
          hasFirstPartyOffer
          offers
          campaignRewards {
            campaignId
            campaignType
            rewardAmount
            rewardAmountType
            rewardUnlockStrategy
            campaignStatus
          }
        }
        campaignRewards {
          campaignId
          campaignType
          rewardAmount
          rewardAmountType
          rewardUnlockStrategy
          campaignStatus
        }
        rollupParentSku
        retailerProductPageUrl
        dtcProductPageUrl
        products {
          retailerSku
          retailPrice
        }
        activeProductPromotion {
          promoType
          isActive
          listPrice
          promoPrice
        }
        childProductMinRetailPrice
        childProductMaxRetailPrice
      }
      facets
      suggestedTerm
    }
  }
`;

export const LLM_SEARCH_PRODUCTS = /* GraphQL */ `
  query SemanticProductSearch(
    $titleInclude: [String!]
    $titleExclude: [String!]
    $stacklineSkus: [String!]
    $stacklineSkusExclude: [String!]
    $campaignTypes: [String!]
    $retailPrice: [RangeFilter!]
    $reviewsRating: RangeFilter
    $reviewsCount: RangeFilter
    $brandIds: [Float!]
    $departmentIds: [Float!]
    $categoryIds: [Float!]
    $subCategoryIds: [Float!]
    $retailerIds: [Float!]
    $isBrandClubReady: Boolean
    $totalRewardAmount: RangeFilter
    $page: PageFilter!
    $sort: [SortFilter!]
    $facetFieldNames: [String!]
    $computeFacets: Boolean!
    $applySpellingCheck: Boolean
    $skipAvailabilityCheck: Boolean
    $imageData: String
    $imageMimeType: String
  ) {
    ${ProductOperation.SemanticProductSearch}(
      titleInclude: $titleInclude
      titleExclude: $titleExclude
      stacklineSkus: $stacklineSkus
      stacklineSkusExclude: $stacklineSkusExclude
      campaignTypes: $campaignTypes
      retailPrice: $retailPrice
      reviewsRating: $reviewsRating
      totalRewardAmount: $totalRewardAmount
      reviewsCount: $reviewsCount
      brandIds: $brandIds
      categoryIds: $categoryIds
      departmentIds: $departmentIds
      subCategoryIds: $subCategoryIds
      retailerIds: $retailerIds
      isBrandClubReady: $isBrandClubReady
      page: $page
      sort: $sort
      facetFieldNames: $facetFieldNames
      computeFacets: $computeFacets
      applySpellingCheck: $applySpellingCheck
      skipAvailabilityCheck: $skipAvailabilityCheck
      imageData: $imageData
      imageMimeType: $imageMimeType
    ) {
      products {
        checkoutEligibility
        stacklineSku
        title
        brandName
        brandId
        categoryId
        subCategoryId
        categoryName
        retailerId
        retailerSku
        retailPrice
        reviewsRating
        groupId
        groupName
        reviewsCount
        searchScore
        totalRewardAmount
        isAvailable
        hasFirstPartyOffer
        offers
        scores {
          maxPurchaseRewardPercent
          maxPurchaseRewardAmount
          maxAffiliateRewardPercent
          maxAffiliateRewardAmount
          totalReviewRewardFeesAmount
        }
        hasActiveInstantReward
        featureBullets
        productPromotions {
          promoType
          isActive
          listPrice
          promoPrice
        }
        imageUrls
        matchedProducts {
          retailPrice
          retailerId
          retailerProductPageUrl
          dtcProductPageUrl
          totalRewardAmount
          stacklineSku
          retailerSku
          checkoutEligibility
          isAvailable
          hasFirstPartyOffer
          offers
          campaignRewards {
            campaignId
            campaignType
            rewardAmount
            rewardAmountType
            rewardUnlockStrategy
            campaignStatus
          }
        }
        campaignRewards {
          campaignId
          campaignType
          rewardAmount
          rewardAmountType
          rewardUnlockStrategy
          campaignStatus
        }
        rollupParentSku
        retailerProductPageUrl
        dtcProductPageUrl
        products {
          retailerSku
          retailPrice
        }
        activeProductPromotion {
          promoType
          isActive
          listPrice
          promoPrice
        }
        childProductMinRetailPrice
        childProductMaxRetailPrice
      }
      facets
      suggestedTerm
      summaryText
    }
  }
`;

export const searchBrandclubProducts = async ({
  stacklineSkus,
  signal,
  isLoggedIn,
  isSemanticProductSearch,
}: {
  stacklineSkus: string[];
  signal?: AbortSignal;
  isLoggedIn: boolean;
  isSemanticProductSearch: boolean;
}): Promise<{ brandclubProducts?: ProductRewardSearchResult[] }> => {
  if (!stacklineSkus) {
    return { brandclubProducts: undefined };
  }
  try {
    const SearchQuery = isSemanticProductSearch
      ? LLM_SEARCH_PRODUCTS
      : isLoggedIn
      ? AUTHENTICATED_SEARCH_PRODUCTS
      : UNAUTHENTICATED_SEARCH_PRODUCTS;
    const RES_KEY = isSemanticProductSearch
      ? ProductOperation.SemanticProductSearch
      : isLoggedIn
      ? ProductOperation.AuthenticatedSearch
      : ProductOperation.UnauthenticatedSearch;
    const data = await graphqlFetch({
      operationName: `${RES_KEY}-${stacklineSkus.join(",")}`,
      query: SearchQuery,
      variables: {
        page: { start: 0, size: stacklineSkus.length },
        stacklineSkus,
        computeFacets: false,
        skipAvailabilityCheck: true,
      },
      signal,
    });
    return { brandclubProducts: data?.data?.[RES_KEY]?.products };
  } catch (error) {
    console.error("Error fetching brandclub products:", error);
    return { brandclubProducts: undefined };
  }
};
